import React from "react";
import {
    AppLayout,
    Box,
    Button,
    Header,
    Link,
    SpaceBetween,
    StatusIndicator,
    Table,
} from "@amzn/awsui-components-react-v3";
import SideBar from "../../navigation/sidebar.js";
import AppContext from "../../context/AppContext";
import {getSidebarItemFromPageStage} from "../home/home";

interface OrphanedSandboxInfo {
    sandboxId: string;
    ownerAlias: string;
    sandboxStatus: string;
    sandboxProperties?: {
        sandboxInstanceType: string;
        diskSizeInGB: number;
    };
    lastEventTime?: string;
}

interface DeletionState {
    deleting?: boolean;
    deleted?: boolean;
    authorizationUrl?: string;
    error?: string;
}

interface OrphanedSandboxesState {
    loading: boolean;
    orphanedSandboxes: OrphanedSandboxInfo[];
    errorMessage: string;
    hasLoaded: boolean;
    sortingColumn: any;
    sortingDescending: boolean;
    deletionStates: Record<string, DeletionState>;
}

class OrphanedSandboxes extends React.Component<{}, OrphanedSandboxesState> {
    static contextType = AppContext;

    constructor(props: {}) {
        super(props);
        this.state = {
            loading: false,
            orphanedSandboxes: [],
            errorMessage: "",
            hasLoaded: false,
            sortingColumn: { sortingField: "ownerAlias" },
            sortingDescending: false,
            deletionStates: {},
        };
    }

    async loadOrphanedSandboxes() {
        const { sandboxManagementClient } = this.context;
        const userAlias = localStorage.getItem("userId");
        const accountId = localStorage.getItem("account");

        if (!sandboxManagementClient || !userAlias || !accountId) {
            this.setState({ errorMessage: "Please select an account first." });
            return;
        }

        this.setState({ loading: true, errorMessage: "", deletionStates: {} });

        try {
            const response = await sandboxManagementClient.listOrphanedSandboxesForAccount(accountId, userAlias);
            if (!response || response.status !== 200) {
                this.setState({
                    errorMessage: "Failed to load orphaned sandboxes. Please try again.",
                    orphanedSandboxes: [],
                    hasLoaded: false,
                });
                return;
            }
            const infos = response.data?.orphanedSandboxInfos ?? [];
            this.setState({ orphanedSandboxes: infos, hasLoaded: true });
        } catch (err) {
            console.error("Failed to load orphaned sandboxes", err);
            this.setState({
                errorMessage: "Failed to load orphaned sandboxes. Please try again.",
                orphanedSandboxes: [],
                hasLoaded: false,
            });
        } finally {
            this.setState({ loading: false });
        }
    }

    async deleteSandbox(sandboxId: string) {
        const { sandboxManagementClient } = this.context;
        const userAlias = localStorage.getItem("userId");
        const accountId = localStorage.getItem("account");

        if (!sandboxManagementClient || !userAlias || !accountId) {
            this.updateDeletionState(sandboxId, { deleting: false, error: "Please select an account first." });
            return;
        }

        this.updateDeletionState(sandboxId, { deleting: true, error: undefined, authorizationUrl: undefined });

        try {
            const response = await sandboxManagementClient.deleteOrphanedSandbox(accountId, userAlias, sandboxId);

            if (response.status === 200) {
                // Deletion initiated — update the row's status to Deleting (matches regular delete pattern)
                this.setState((prev) => ({
                    orphanedSandboxes: prev.orphanedSandboxes.map(s =>
                        s.sandboxId === sandboxId ? { ...s, sandboxStatus: "Deleting" } : s
                    ),
                    deletionStates: { ...prev.deletionStates, [sandboxId]: { deleting: false, deleted: true } },
                }));
            } else if (response.status === 403 && response.data?.authorizationUrl) {
                // CAZ required — show authorization link
                this.updateDeletionState(sandboxId, {
                    deleting: false,
                    authorizationUrl: response.data.authorizationUrl,
                });
            } else {
                // Other error
                const errMsg = response?.data?.message || "Deletion failed. Please try again.";
                this.updateDeletionState(sandboxId, { deleting: false, error: errMsg });
            }
        } catch (err) {
            console.error("Failed to delete sandbox", err);
            this.updateDeletionState(sandboxId, { deleting: false, error: "Deletion failed." });
        }
    }

    updateDeletionState(sandboxId: string, update: Partial<DeletionState>) {
        this.setState((prev) => ({
            deletionStates: {
                ...prev.deletionStates,
                [sandboxId]: { ...prev.deletionStates[sandboxId], ...update },
            },
        }));
    }

    renderActions(item: OrphanedSandboxInfo) {
        const state = this.state.deletionStates[item.sandboxId] || {};

        // Sandbox is being deleted (from API response or status update)
        if (state.deleted || item.sandboxStatus === "Deleting") {
            return <StatusIndicator type="success">Deleting</StatusIndicator>;
        }

        if (state.error) {
            return (
                <SpaceBetween size="xs" direction="vertical">
                    <StatusIndicator key="error" type="error">{state.error}</StatusIndicator>
                    <Button key="retry" variant="link" onClick={() => this.deleteSandbox(item.sandboxId)}>Retry</Button>
                </SpaceBetween>
            );
        }

        if (state.authorizationUrl) {
            // CAZ required — user needs to complete 2PR at Shoehorn, then retry
            return (
                <SpaceBetween size="xs" direction="vertical">
                    <StatusIndicator key="warning" type="warning">Approval required</StatusIndicator>
                    <Link key="link" href={state.authorizationUrl} external>Complete approval</Link>
                    <Button
                        key="retry"
                        variant="primary"
                        onClick={() => this.deleteSandbox(item.sandboxId)}
                    >
                        Retry Delete
                    </Button>
                </SpaceBetween>
            );
        }

        // Default: single Delete button
        return (
            <Button
                variant="normal"
                loading={state.deleting}
                onClick={() => this.deleteSandbox(item.sandboxId)}
            >
                Delete
            </Button>
        );
    }

    getSortedItems() {
        const { orphanedSandboxes, sortingColumn, sortingDescending } = this.state;
        if (!sortingColumn || !sortingColumn.sortingField) return orphanedSandboxes;

        const field = sortingColumn.sortingField;
        const sorted = [...orphanedSandboxes].sort((a, b) => {
            let valA, valB;
            if (field === "instanceType") {
                valA = a.sandboxProperties?.sandboxInstanceType || "";
                valB = b.sandboxProperties?.sandboxInstanceType || "";
            } else if (field === "status") {
                valA = a.sandboxStatus || "";
                valB = b.sandboxStatus || "";
            } else {
                valA = a[field] || "";
                valB = b[field] || "";
            }
            return valA.localeCompare(valB);
        });
        return sortingDescending ? sorted.reverse() : sorted;
    }

    render() {
        const { loading, orphanedSandboxes, errorMessage, hasLoaded } = this.state;

        return (
            <AppLayout
                navigation={<SideBar activeHref="/orphaned-sandboxes" items={getSidebarItemFromPageStage(this.context.pageStage)} />}
                content={
                    <SpaceBetween size="l">
                        <Header
                            key="header"
                            variant="h1"
                            description="Sandboxes whose owners are no longer on the account team. These consume resources and should be cleaned up."
                            actions={
                                <Button
                                    variant="primary"
                                    loading={loading}
                                    onClick={() => this.loadOrphanedSandboxes()}
                                >
                                    Check for Orphaned Sandboxes
                                </Button>
                            }
                        >
                            Orphaned Sandboxes
                        </Header>

                        {errorMessage && (
                            <StatusIndicator key="page-error" type="error">{errorMessage}</StatusIndicator>
                        )}

                        {hasLoaded && (
                            <Table
                                key="table"
                                trackBy="sandboxId"
                                columnDefinitions={[
                                    {
                                        id: "sandboxId",
                                        header: "Sandbox Name",
                                        cell: (item: OrphanedSandboxInfo) => item.sandboxId,
                                        sortingField: "sandboxId",
                                    },
                                    {
                                        id: "ownerAlias",
                                        header: "Original Owner",
                                        cell: (item: OrphanedSandboxInfo) => item.ownerAlias,
                                        sortingField: "ownerAlias",
                                    },
                                    {
                                        id: "instanceType",
                                        header: "Instance Type",
                                        cell: (item: OrphanedSandboxInfo) =>
                                            item.sandboxProperties?.sandboxInstanceType || "-",
                                        sortingField: "instanceType",
                                    },
                                    {
                                        id: "diskSize",
                                        header: "Disk (GB)",
                                        cell: (item: OrphanedSandboxInfo) =>
                                            item.sandboxProperties?.diskSizeInGB?.toString() || "-",
                                    },
                                    {
                                        id: "status",
                                        header: "Status",
                                        cell: (item: OrphanedSandboxInfo) => item.sandboxStatus,
                                        sortingField: "status",
                                    },
                                    {
                                        id: "lastEventTime",
                                        header: "Last Activity",
                                        cell: (item: OrphanedSandboxInfo) => item.lastEventTime || "-",
                                        sortingField: "lastEventTime",
                                    },
                                    {
                                        id: "actions",
                                        header: "Actions",
                                        cell: (item: OrphanedSandboxInfo) => this.renderActions(item),
                                    },
                                ]}
                                items={this.getSortedItems()}
                                loading={loading}
                                loadingText="Checking for orphaned sandboxes..."
                                sortingColumn={this.state.sortingColumn}
                                sortingDescending={this.state.sortingDescending}
                                onSortingChange={(event) => {
                                    this.setState({
                                        sortingColumn: event.detail.sortingColumn,
                                        sortingDescending: event.detail.isDescending,
                                    });
                                }}
                                empty={
                                    <Box textAlign="center" color="inherit">
                                        <b>No orphaned sandboxes found</b>
                                        <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                                            All sandbox owners are still on the account team.
                                        </Box>
                                    </Box>
                                }
                                header={
                                    <Header counter={`(${orphanedSandboxes.length})`}>
                                        Orphaned Sandboxes
                                    </Header>
                                }
                            />
                        )}
                    </SpaceBetween>
                }
                toolsHide={true}
            />
        );
    }
}

export default OrphanedSandboxes;
